import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Button,
  Input,
  Row,
  Col,
  Container,
} from "reactstrap";
import {
  getProspectEngagementDashboard,
  importLeads,
  refreshDeliveryStatus,
} from "../service";
import { Notification } from "../../common/style";

const ProspectEngagement = () => {
  const [prospects, setProspects] = useState([]);
  const [search, setSearch] = useState("");
  const [templateFilter, setTemplateFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [activityFilter, setActivityFilter] = useState("All Orgs");
  const [metFilter, setMetFilter] = useState("All");
  const [demoFilter, setDemoFilter] = useState("All");
  const [isNotificationVisible, setIsNotificationVisible] = useState(true);
  const [notificationMessage, setNotificationMessage] = useState({});
  const fileInputRef = useRef(null);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(150); // Fixed at 150 items per page
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    getProspectEngagementDashboard().then((res) => {
      setProspects(res);
    });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    setSearch(params.get("search") || "");
    setTemplateFilter(params.get("template") || "All");
    setStatusFilter(params.get("status") || "All");
    setActivityFilter(params.get("activity") || "All Orgs");
    setMetFilter(params.get("met") || "All");
    setDemoFilter(params.get("demo") || "All");

    getProspectEngagementDashboard().then((res) => {
      setProspects(res);
    });
  }, []);

  useEffect(() => updateQueryParam("search", search), [search]);
  useEffect(() => updateQueryParam("template", templateFilter), [templateFilter]);
  useEffect(() => updateQueryParam("status", statusFilter), [statusFilter]);
  useEffect(() => updateQueryParam("activity", activityFilter), [activityFilter]);
  useEffect(() => updateQueryParam("met", metFilter), [metFilter]);
  useEffect(() => updateQueryParam("demo", demoFilter), [demoFilter]);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const toggleUsers = (id, e) => {
    e.stopPropagation();
    setExpandedUsers((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const updateQueryParam = (key, value) => {
    const params = new URLSearchParams(window.location.search);
    if (value === "All" || value === "" || value === "All Orgs") {
      params.delete(key);
    } else {
      params.set(key, value);
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
  };

  // Get unique template names and delivery statuses from data
  const uniqueTemplates = [
    ...new Set(prospects.map((p) => p.last_message?.template).filter(Boolean)),
  ];
  const uniqueStatuses = [
    ...new Set(prospects.map((p) => p.last_message?.delivery_status).filter(Boolean)),
  ];

  // Filtered prospects based on search and filters
  const filteredProspects = prospects.filter((p) => {
    const matchesSearch = p.customer?.toLowerCase().includes(search.toLowerCase());
    const matchesTemplate =
      templateFilter === "All" || p.last_message?.template === templateFilter;
    const matchesStatus =
      statusFilter === "All" || p.last_message?.delivery_status === statusFilter;
    let matchesActivity = true;
    if (activityFilter === "Leads") {
      matchesActivity = p.status === "lead";
    } else if (activityFilter === "Active Prospects") {
      matchesActivity = p.status === "prospect" && p.active;
    } else if (activityFilter === "Inactive Prospects") {
      matchesActivity = p.status === "prospect" && !p.active;
    }
    const matchesMet =
      metFilter === "All" || (metFilter === "Met" ? p.met : !p.met);
    const matchesDemo =
      demoFilter === "All" || (demoFilter === "Demo" ? p.demo : !p.demo);
    return matchesSearch && matchesTemplate && matchesStatus && matchesActivity && matchesMet && matchesDemo;
  });

  const formatShortDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      month: "short",
      day: "numeric",
    });
  };

  const formatShortDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProspects.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredProspects.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      alert("No file selected.");
      return;
    }

    if (!file.name.endsWith(".csv")) {
      alert("Please upload a valid CSV file.");
      return;
    }

    try {
      const result = await importLeads(file);

      const refreshed = await getProspectEngagementDashboard();
      setProspects(refreshed);

      setIsNotificationVisible(true);
      setNotificationMessage({
        text: result.message || "CSV imported successful.",
        type: "success",
      });
    } catch (error) {
      const message =
        typeof error === "string"
          ? error
          : error?.message || error?.error || "Import failed. Try again.";

      setIsNotificationVisible(true);
      setNotificationMessage({
        text: message,
        type: "danger",
      });
    }
  };

  const handleRefreshStatus = async () => {
    setIsRefreshing(true);
    try {
      await refreshDeliveryStatus();

      setIsNotificationVisible(true);
      setNotificationMessage({
        text: "Delivery status refreshed successfully",
        type: "success",
      });

      const refreshedData = await getProspectEngagementDashboard();
      setProspects(refreshedData);
    } catch (error) {
      setIsNotificationVisible(true);
      setNotificationMessage({
        text: error.message || "Failed to refresh status",
        type: "danger",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Container fluid className="py-4 px-3">
      {notificationMessage.text && (
        <Notification
          color={notificationMessage.type}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notificationMessage.text}
        </Notification>
      )}

      <div className="d-flex justify-content-between align-items-center">
        {" "}
        <h5 className="mb-4">Prospect Engagement</h5>
        <div className="mb-3 text-end pe-2">
          <Button
            color="outline-primary"
            className="me-2 mb-0"
            onClick={handleRefreshStatus}
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Refreshing...
              </>
            ) : (
              <>
                <i className="fas fa-sync-alt me-1"></i> Refresh Status
              </>
            )}
          </Button>
          <Button
            color="outline-secondary"
            className="me-2 mb-0"
            onClick={handleFileButtonClick}
          >
            Import
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
        </div>
      </div>

      {/* Filters and Search */}
      <Row className="g-3 mb-4 align-items-end flex-nowrap overflow-auto">
        <Col
          xs="12"
          sm="6"
          md="3"
          lg="2"
          className="flex-shrink-0"
          style={{ minWidth: 150, maxWidth: 200 }}
        >
          <Input
            type="search"
            placeholder="Search customer name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="form-control"
            style={{ fontSize: "1rem" }}
          />
        </Col>
        <Col xs="12" sm="6" md="3" lg="2" className="flex-shrink-0" style={{ minWidth: 150, maxWidth: 200 }}>
          <Input
            type="select"
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
            className="form-select"
            style={{ fontSize: "1rem" }}
          >
            <option value="All Orgs">All Orgs</option>
            <option value="Leads">Leads</option>
            <option value="Active Prospects">Active Prospects</option>
            <option value="Inactive Prospects">Inactive Prospects</option>
          </Input>
        </Col>
        <Col xs="12" sm="6" md="3" lg="2" className="flex-shrink-0" style={{ minWidth: 150, maxWidth: 200 }}>
          <Input
            type="select"
            value={metFilter}
            onChange={(e) => setMetFilter(e.target.value)}
            className="form-select"
            style={{ fontSize: "1rem" }}
          >
            <option value="All">All Met</option>
            <option value="Met">Met</option>
            <option value="Not Met">Not Met</option>
          </Input>
        </Col>
        <Col xs="12" sm="6" md="3" lg="2" className="flex-shrink-0" style={{ minWidth: 150, maxWidth: 200 }}>
          <Input
            type="select"
            value={demoFilter}
            onChange={(e) => setDemoFilter(e.target.value)}
            className="form-select"
            style={{ fontSize: "1rem" }}
          >
            <option value="All">All Demo</option>
            <option value="Demo">Demo</option>
            <option value="No Demo">No Demo</option>
          </Input>
        </Col>
        <Col xs="12" sm="6" md="3" lg="2" className="flex-shrink-0" style={{ minWidth: 150, maxWidth: 200 }}>
          <Input
            type="select"
            value={templateFilter}
            onChange={(e) => setTemplateFilter(e.target.value)}
            className="form-select"
            style={{ fontSize: "1rem" }}
          >
            <option value="All">All Templates</option>
            {uniqueTemplates.map((tpl) => (
              <option key={tpl} value={tpl}>{tpl}</option>
            ))}
          </Input>
        </Col>
        <Col xs="12" sm="6" md="3" lg="2" className="flex-shrink-0" style={{ minWidth: 150, maxWidth: 200 }}>
          <Input
            type="select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-select"
            style={{ fontSize: "1rem" }}
          >
            <option value="All">All Message Status</option>
            {uniqueStatuses.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </Input>
        </Col>
      </Row>

      <Table bordered responsive hover className="align-middle w-100 small" style={{ minWidth: 900, fontSize: '12px' }}>
        <thead className="table-secondary">
          <tr>
            <th>Customer</th>
            <th>Users</th>
            <th>Status</th>
            <th>City</th>
            <th>Last template</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((p, idx) => (
              <tr
                key={p.id}
                className={idx % 2 === 0 ? "table-light" : ""}
              >
                <td className="py-3 px-4">
                  {p.customer} #({p.id})
                </td>
                <td className="py-3 px-4">
                  {p.users_info ? (
                    <>
                      <a
                        href="#!"
                        onClick={(e) => toggleUsers(p.id, e)}
                        className="text-primary text-decoration-none"
                      >
                        {expandedUsers[p.id] ? "Hide users" : "Show users"}
                      </a>
                      {expandedUsers[p.id] && (
                        <div className="mt-2 small text-muted">
                          {p.users_info}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-muted">No users</span>
                  )}
                </td>{" "}
                <td className="py-3 px-4">{p.status}</td>
                <td className="py-3 px-4">{p.city}</td>
                <td className="py-3 px-4">
                  {p.last_message ? (
                    <>
                      <div>{p.last_message.template}</div>
                      <div className="text-muted small">
                        {formatShortDateTime(p.last_message.sent_at)} ({p.last_message.delivery_status})
                      </div>
                    </>
                  ) : (
                    <span className="text-muted">No messages</span>
                  )}
                </td>
              </tr>
          ))}
        </tbody>
      </Table>
      {filteredProspects.length > itemsPerPage && (
        <div className="d-flex justify-content-center mt-3">
          <nav>
            <ul className="pagination">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (number) => (
                  <li
                    key={number}
                    className={`page-item ${
                      currentPage === number ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => paginate(number)}
                    >
                      {number}
                    </button>
                  </li>
                )
              )}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </Container>
  );
};

export default ProspectEngagement;
